import { isNil } from "lodash-es";
import moment from "moment-timezone";
import classnames from "classnames";
import React, { useEffect, useId } from "react";
import Flatpickr from "react-flatpickr";
import { FormText } from "reactstrap";
import { InputWrapper } from "./InputWrapper";

export const DateInput = ({ children, input, label, meta, className, ...rest }) => {
  const flatpickrRef = React.useRef(null);
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  const displayInputId = `${inputId}-display`;
  const testId = rest["data-testid"];

  // This is a hack to cover some of the functionality breakage in Flatpickr. This appears to be fixed in 4.6.5 per
  // https://github.com/flatpickr/flatpickr/pull/2130
  useEffect(() => {
    const fp = flatpickrRef.current.flatpickr;

    fp.input.type = "hidden"; // Something is causing this to get lost. Reset it.
    fp.altInput.id = displayInputId;
    if (testId) fp.altInput.dataset.testid = `${testId}-display`;

    fp.altInput.addEventListener("blur", (e) => {
      if (fp.calendarContainer.contains(e.relatedTarget)) return; // If clicking into calendar, don't override it
      fp.setDate(fp.altInput.value, true, fp.config.altFormat); // Ensure date is set in altFormat, bubble up to change
    });
  }, [displayInputId, testId, input]);

  return (
    <InputWrapper testId={testId} label={label} required={rest.required} htmlFor={displayInputId}>
      <Flatpickr
        ref={flatpickrRef}
        className={classnames("form-control", className, {
          "is-invalid": Boolean(meta.touched && meta.error),
        })}
        hidden // https://github.com/haoxins/react-flatpickr/issues/213 https://github.com/flatpickr/flatpickr/issues/2110
        data-enable-time={rest.isDateTime}
        placeholder="MM/DD/YYYY"
        options={{
          allowInput: true,
          altInput: true, // See bug with `hidden` if we remove this
          altFormat: "m/d/Y",
          dateFormat: "Y-m-d",
          parseDate: (str) => {
            // we run through this function twice, once when the user inputs and again from the onChange function.
            // this needs to handle user inputted string as well as altFormat and dateFormat
            const enteredDate = moment(str, "MM/DD/YYYY");
            const isoDate = moment(str, "YYYY-MM-DD");

            return enteredDate.isValid() ? enteredDate.toDate() : isoDate.toDate();
          },
        }}
        {...input}
        {...rest}
        id={inputId}
        onChange={(dates) => {
          if (isNil(dates) || dates.length < 1) {
            input.onChange(null);
          } else {
            // this sends through parseDate and needs to be in dateFormat
            input.onChange(moment(dates[0]).format("YYYY-MM-DD"));
          }
        }}
      />
      {meta.touched && meta.error && <FormText color="danger">{meta.error}</FormText>}
      {children}
    </InputWrapper>
  );
};
